import React, {useEffect, useRef} from 'react'
import {BoxProps, HStack, useMultiStyleConfig, VStack, Text, Divider, Box, Popover, PopoverTrigger, PopoverContent, useDisclosure} from '@chakra-ui/react'
import {ApplePayWrapper, Button, ColorCircle, Icon, PayPalButtonWrapper} from '_uiComponents'
import {ProductVariant, ProductVariationGroup} from '_types'
import {useGlobalConfigState} from '_context'
import {useIntl} from 'react-intl'
import {useBasket, useAnalytics} from '_customHooks'

// This is the mobile add-to-cart confirmation layer (manuale BitBang PDP PWA §3.1.6): any add-to-cart
// started from here reports the analytics origin 'confirm_layer_add_to_cart' (dimension22).
const ADD_TO_CART_ORIGIN = 'confirm_layer_add_to_cart' as const

// Controls where the CTA button sits:
// - 'inflow': in the normal flow (initial state, above the color section)
// - 'fixed':  pinned to the bottom of the viewport (after the user scrolls)
// - 'hidden': not shown (near the footer)
export type StickyAddToCartPositionMode = 'inflow' | 'fixed' | 'hidden'

interface StickyAddToCartProps {
    selectedVariant?: ProductVariant
    activeVariation?: ProductVariationGroup
    productName?: string
    productPrice?: string
    productPriceNumber?: number
    onAddToCart: (origin: typeof ADD_TO_CART_ORIGIN) => void
    // Defaults to 'fixed' to preserve the previous always-pinned behaviour for any other caller
    // (e.g. the zoom modal footer). The PDP passes the scroll-driven mode.
    positionMode?: StickyAddToCartPositionMode
}

/**
 * @name StickyAddToCart
 *
 * @component
 *
 * @description
 * Renders the size selector for a product
 *
 * @returns {React.FC} - The rendered product size selector component.
 */
const StickyAddToCart: React.FC<StickyAddToCartProps> = ({
    selectedVariant,
    activeVariation,
    productName,
    productPrice,
    productPriceNumber,
    onAddToCart,
    positionMode = 'fixed',
}: StickyAddToCartProps) => {
    const {formatMessage} = useIntl()
    const {isOpen: isPopoverOpen, onToggle: onPopoverToggle, onClose: onPopoverClose} = useDisclosure()
    const shouldOpenAfterSizeSelection = useRef<boolean>(false)
    const {basket, addItemToBasket, updateItemInBasket, removeItemFromBasket} = useBasket()
    const {analyticsEventConfirmLayerAddToCart} = useAnalytics()

    // SKU used as eventLabel for the confirmation-layer events (manuale BitBang PDP PWA §3.1.6).
    // Prefer the selected variant's full SKU (color + size, e.g. "262M0B137427C00101"); fall back to the
    // master/variation-group code only when no size has been selected yet.
    const analyticsSku = selectedVariant?.ID ?? activeVariation?.analytics?.productId ?? activeVariation?.ID ?? ''

    const buyBtnRef = useRef<HTMLButtonElement>(null)

    const {appPreferences} = useGlobalConfigState()

    const paypalPrefs = {
        PAYPAL_EXPRESS_Disable_Funds: appPreferences.PAYPAL_EXPRESS_Disable_Funds,
        PAYPAL_EXPRESS_Allowed_Currencies: appPreferences.PAYPAL_EXPRESS_Allowed_Currencies,
        PP_API_PaymentAction: appPreferences.PP_API_PaymentAction,
        PP_API_Button_Location: appPreferences.PP_API_Button_Location,
        paypal_clientId: appPreferences.paypal_clientId,
        paypal_button_style: appPreferences.paypal_button_style,
        ShippingMethod_NI: appPreferences.ShippingMethod_NI,
    }

    const applePayPrefs = {
        Allowed_Suretax_Dispatch: appPreferences.Allowed_Suretax_Dispatch,
        ApplePay_Enabled: appPreferences.ApplePay_Enabled,
        ApplePay_MerchantId: appPreferences.ApplePay_MerchantId,
        ApplePay_MerchantName: appPreferences.ApplePay_MerchantName,
        ApplePay_MerchantCapabilities: appPreferences.ApplePay_MerchantCapabilities,
        ApplePay_SupportedNetworks: appPreferences.ApplePay_SupportedNetworks,
        hostLink: appPreferences.hostLink,
    }

    const showPaypalButton =
        !!basket?.currency &&
        !!paypalPrefs.PAYPAL_EXPRESS_Allowed_Currencies?.includes(basket?.currency) &&
        !!paypalPrefs.PP_API_Button_Location?.[0]?.toLowerCase()?.includes('pdp') &&
        !!paypalPrefs.paypal_clientId &&
        !!paypalPrefs.paypal_button_style

    const showApplePayButton =
        basket && !!basket?.currency && applePayPrefs.ApplePay_Enabled && typeof window !== 'undefined' && window?.ApplePaySession?.canMakePayments()

    const styles = useMultiStyleConfig('ProductStickyAddToCart', {isOpen: isPopoverOpen}) as Record<string, BoxProps>

    // Merge the shared button base with the mode-specific position styles.
    const buyButtonModeStyle = positionMode === 'fixed' ? styles.buyButtonFixed : positionMode === 'hidden' ? styles.buyButtonHidden : styles.buyButtonInflow
    const buyButtonSx = {...styles.buyButtonSticky, ...buyButtonModeStyle}

    const addProductToBasket = (): Promise<void> => {
        return addItemToBasket([{productId: selectedVariant?.ID || '', quantity: 1, price: productPriceNumber}], false)
    }

    const removeProductFromBasket = (basketItemId: string, newQuantity: number): Promise<void> => {
        return newQuantity > 0 ? updateItemInBasket(basketItemId, newQuantity) : removeItemFromBasket(basketItemId)
    }

    const LABEL_BUY_BUTTON = formatMessage({id: 'button.buynow', defaultMessage: 'BUY'})?.toUpperCase()
    const LABEL_ADD_TO_CART = formatMessage({id: 'buy.button', defaultMessage: 'ADD TO CART'})?.toUpperCase()

    const handleClosePopover = () => {
        // Manuale BitBang PDP PWA §3.1.6 — confirmation layer close (mobile only).
        analyticsEventConfirmLayerAddToCart({action: 'close', sku: analyticsSku})
        /**
         * IMPORTANT
         * the setTimeout 0 fixes a problem on safari ios where the paypal button (iframe) is not clickable and
         * when clicking on it the popover closes instead. so keep the setTimeout.
         */
        setTimeout(() => {
            document.body.style.overflow = ''
            onPopoverClose()
        }, 0)
    }

    const handleOpenPopover = () => {
        if (selectedVariant) {
            document.body.style.overflow = 'hidden'
            // Manuale BitBang PDP PWA §3.1.6 — confirmation layer open (mobile only).
            analyticsEventConfirmLayerAddToCart({action: 'open', sku: analyticsSku})
            onPopoverToggle()
        }
    }

    const getPopoverOffetInflowMode = (): [number, number] => {
        const defaultOffset: [number, number] = [0, -80]
        if (!buyBtnRef.current) return defaultOffset

        return [0, -(window.innerHeight - buyBtnRef.current.offsetTop + 8)]
    }

    useEffect(() => {
        if (selectedVariant && shouldOpenAfterSizeSelection.current) {
            setTimeout(handleOpenPopover, 500)
            shouldOpenAfterSizeSelection.current = false
        }
    }, [selectedVariant])

    return (
        <Box data-testid="sticky-add-to-cart" sx={styles.stickyAddToCartContainer}>
            {/* Explicit tap-outside overlay. We disable Chakra's closeOnBlur because it treats a click
                on the cross-origin PayPal iframe as an "outside" interaction and closes the popover,
                unmounting the iframe before the PayPal flow starts (the button appears to do nothing).
                This overlay restores tap-outside-to-close for real outside taps only; the PayPal iframe
                sits above it (popover zIndex 150000) so clicking PayPal no longer closes the layer. */}
            {isPopoverOpen && <Box data-testid="sticky-add-to-cart-overlay" sx={styles.overlay} onClick={handleClosePopover} />}
            <Popover
                variant={'pdpStickyMobile'}
                offset={positionMode === 'fixed' ? [0, -80] : getPopoverOffetInflowMode()}
                isOpen={isPopoverOpen}
                onClose={handleClosePopover}
                onOpen={handleOpenPopover}
                closeOnBlur={false}
            >
                <PopoverTrigger>
                    <Button
                        size="xs"
                        variant="primary"
                        sx={buyButtonSx}
                        ref={buyBtnRef}
                        onClick={() => {
                            if (!selectedVariant) {
                                onAddToCart(ADD_TO_CART_ORIGIN)
                                shouldOpenAfterSizeSelection.current = true
                            } else {
                                onPopoverToggle()
                            }
                        }}
                    >
                        {LABEL_BUY_BUTTON}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="cc-confirm-layer-popover" sx={styles.popover}>
                    <VStack {...styles.wrapper}>
                        <VStack {...styles.nameAndPriceContainer}>
                            <Text {...styles.productName}>{productName}</Text>
                            <Text {...styles.productPrice}>{productPrice}</Text>
                        </VStack>

                        <Divider />

                        <VStack {...styles.colorAndSizeContainer}>
                            <HStack {...styles.variationAttributeContainer}>
                                <Text {...styles.variationAttributeText}>{activeVariation?.color?.label}</Text>
                                <Button variant="round-swatch" aria-label={activeVariation?.color.label} aria-pressed={true}>
                                    <ColorCircle hexCodes={activeVariation?.color?.colorHexCode ?? null} selected />
                                </Button>
                            </HStack>
                            <HStack {...styles.variationAttributeContainer}>
                                <Text {...styles.variationAttributeText}>Taglia</Text>
                                <Text {...styles.variationAttributeText}>{selectedVariant?.size?.label}</Text>
                            </HStack>
                        </VStack>

                        <Divider />

                        <VStack {...styles.buyButtonsContainer}>
                            <Button
                                as={HStack}
                                size="sm"
                                variant="primary"
                                sx={styles.addToCartButton}
                                onClick={() => {
                                    onAddToCart(ADD_TO_CART_ORIGIN)
                                    // closeOnBlur is disabled (to keep the PayPal iframe from closing the layer),
                                    // so the add-to-cart CTA must close the confirmation layer explicitly.
                                    handleClosePopover()
                                }}
                            >
                                <Text>{LABEL_ADD_TO_CART}</Text>
                                <Icon name="IconCart" size="sm" />
                            </Button>

                            {/* Gate the express buttons on the popover being open so the PayPal SDK
                                mounts its iframe only when the container is visible and measured.
                                On Safari (where Apple Pay is also present) mounting while the popover
                                is display:none makes the PayPal iframe collapse to a zero-height box. */}
                            {isPopoverOpen && showPaypalButton && (
                                // sx (not spread) so the nested `.paypal_container` selector in the theme
                                // is emitted by Emotion — spreading a style object drops nested selectors.
                                <Box sx={styles.fastCheckoutWrapper}>
                                    <PayPalButtonWrapper
                                        sitePreference={paypalPrefs}
                                        basketId={basket?.basketId}
                                        currency={basket?.currency}
                                        subTotal={basket?.c_subtotal?.value}
                                        buttonLocation="pdp"
                                        analyticsAccessType="confirm_layer_add_to_cart"
                                        productId={selectedVariant?.ID}
                                        onBeforeClick={addProductToBasket}
                                        onAfterNotCompleted={removeProductFromBasket}
                                    />
                                </Box>
                            )}

                            {isPopoverOpen && showApplePayButton && (
                                <Box sx={styles.fastCheckoutWrapper}>
                                    <ApplePayWrapper
                                        customPrefs={applePayPrefs}
                                        basketId={basket?.basketId}
                                        currency={basket?.currency}
                                        subTotal={basket?.c_subtotal?.value}
                                        buttonLocation="pdp"
                                        analyticsAccessType="confirm_layer_add_to_cart"
                                        productId={selectedVariant?.ID}
                                        onBeforeClick={addProductToBasket}
                                        onAfterNotCompleted={removeProductFromBasket}
                                    />
                                </Box>
                            )}
                        </VStack>
                    </VStack>
                </PopoverContent>
            </Popover>
        </Box>
    )
}

export default StickyAddToCart
